import type { TideData, TideHourly, TidePrediction } from "@/lib/tides";
import { fontStack } from "@/theme";
import { convertHeight, heightUnit } from "@/lib/units";
import { unitSystemAtom } from "@/atoms/units";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format } from "date-fns";
import { useRecoilValue } from "recoil";
import {
  Area,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Cosine-interpolate hourly tide heights from hi/lo predictions.
 * Tides follow a roughly sinusoidal curve between highs and lows.
 */
function interpolateFromHiLo(predictions: TidePrediction[]): TideHourly[] {
  if (predictions.length < 2) return [];

  const sorted = [...predictions].sort((a, b) => a.time.getTime() - b.time.getTime());
  const results: TideHourly[] = [];
  const startMs = sorted[0].time.getTime();
  const endMs = sorted[sorted.length - 1].time.getTime();

  // Generate one point per hour
  for (let ms = startMs; ms <= endMs; ms += 3600_000) {
    // Find surrounding hi/lo points
    let before = sorted[0];
    let after = sorted[sorted.length - 1];
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].time.getTime() <= ms && sorted[i + 1].time.getTime() >= ms) {
        before = sorted[i];
        after = sorted[i + 1];
        break;
      }
    }

    const segStart = before.time.getTime();
    const segEnd = after.time.getTime();
    const t = segEnd === segStart ? 0 : (ms - segStart) / (segEnd - segStart);

    // Cosine interpolation for smooth sine-like curve
    const cosT = (1 - Math.cos(t * Math.PI)) / 2;
    const height = before.height + (after.height - before.height) * cosT;

    results.push({ time: new Date(ms), height });
  }

  return results;
}

interface TideChartProps {
  tideData: TideData;
}

interface ChartDatum {
  idx: number;
  time: string;
  height: number;
  label: string;
  dayLabel: string;
  hour: number;
}

export default function TideChart({ tideData }: TideChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const units = useRecoilValue(unitSystemAtom);
  const unit = heightUnit(units);

  // Use hourly data when available, otherwise interpolate from hi/lo
  const hourlyPointsAll =
    tideData.hourly.length > 0
      ? tideData.hourly
      : interpolateFromHiLo(tideData.predictions);

  // Clip the horizon based on viewport. A 5-day tide curve crammed into
  // a phone width loses the shape of individual high/low cycles; 3 days
  // is readable and still covers the decision window a surfer cares
  // about (today + next two). Desktop keeps the full 5-day view.
  const horizonDays = isMobile ? 3 : 5;
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() + horizonDays);
  const hourlyPoints = hourlyPointsAll.filter((h) => h.time.getTime() < cutoff.getTime());

  const chartData: ChartDatum[] = hourlyPoints.map((h, i) => ({
    idx: i,
    time: h.time.toISOString(),
    height: convertHeight(h.height, units),
    label: format(h.time, isMobile ? "ha" : "h:mm a"),
    dayLabel: format(h.time, "EEE"),
    hour: h.time.getHours(),
  }));

  if (chartData.length === 0) return null;

  // Find "now" index
  const nowMs = Date.now();
  let nowIdx: number | null = null;
  let minDiff = Infinity;
  chartData.forEach((d, i) => {
    const diff = Math.abs(new Date(d.time).getTime() - nowMs);
    if (diff < minDiff) {
      minDiff = diff;
      nowIdx = i;
    }
  });

  // Build day header boundaries
  const dayHeaders: { label: string; startIdx: number; endIdx: number }[] = [];
  let currentDay = "";
  chartData.forEach((d, i) => {
    const dayKey = format(new Date(d.time), "yyyy-MM-dd");
    if (dayKey !== currentDay) {
      if (dayHeaders.length > 0) {
        dayHeaders[dayHeaders.length - 1].endIdx = i - 1;
      }
      dayHeaders.push({
        label: format(new Date(d.time), "EEE d").toUpperCase(),
        startIdx: i,
        endIdx: chartData.length - 1,
      });
      currentDay = dayKey;
    }
  });

  // Annotate hi/lo points from predictions
  const hiLoLabels: { idx: number; type: "H" | "L"; height: number }[] = [];
  tideData.predictions.forEach((p) => {
    const pMs = p.time.getTime();
    let bestIdx = 0;
    let bestDiff = Infinity;
    chartData.forEach((d, i) => {
      const diff = Math.abs(new Date(d.time).getTime() - pMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    });
    if (bestDiff < 2 * 60 * 60 * 1000) {
      hiLoLabels.push({ idx: bestIdx, type: p.type, height: p.height });
    }
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 1,
        }}
      >
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          Tide
        </Typography>
        <Typography variant="overline" sx={{ color: "ink.muted" }}>
          {tideData.stationName}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          borderTop: "1px solid",
          borderColor: "divider",
          ml: isMobile ? "0px" : "42px",
          mr: isMobile ? "8px" : "42px",
        }}
      >
        {dayHeaders.map((dh, i) => {
          const span = dh.endIdx - dh.startIdx + 1;
          const total = chartData.length;
          return (
            <Box
              key={i}
              sx={{
                flex: `${span} 0 0`,
                width: `${(span / total) * 100}%`,
                py: 0.75,
                pl: 0.5,
                borderRight: i < dayHeaders.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Typography variant="overline" sx={{ color: "ink.muted" }}>
                {dh.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: "100%",
          height: { xs: 140, sm: 180, md: 200 },
          "& .recharts-wrapper, & .recharts-surface, & .recharts-tooltip-wrapper": {
            outline: "none",
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={
              isMobile
                ? { top: 20, right: 8, left: -12, bottom: 5 }
                : { top: 20, right: 30, left: -4, bottom: 5 }
            }
          >
            <defs>
              {/* Tide is the ONE chart DESIGN.md allows an area fill on —
                  subtle accent shading so it reads as water. */}
              <linearGradient id="tideFill" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.palette.accent.main}
                  stopOpacity={0.12}
                />
                <stop
                  offset="100%"
                  stopColor={theme.palette.accent.main}
                  stopOpacity={0.02}
                />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="idx"
              type="number"
              domain={[0, chartData.length - 1]}
              tick={false}
              stroke={theme.palette.rule.main}
              height={8}
            />
            <YAxis
              tick={
                isMobile
                  ? false
                  : {
                      fontSize: 11,
                      fill: theme.palette.ink.muted,
                      fontFamily: fontStack.mono,
                    }
              }
              width={isMobile ? 15 : 40}
              axisLine={false}
              tickLine={false}
              stroke={theme.palette.rule.main}
            />

            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.rule.main}`,
                borderRadius: 4,
                fontSize: 12,
                color: theme.palette.ink.main,
                padding: "8px 10px",
                fontFamily: fontStack.mono,
                boxShadow: "none",
              }}
              labelStyle={{
                fontFamily: fontStack.sans,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: theme.palette.ink.muted,
                marginBottom: 4,
              }}
              formatter={(value: number) => [`${value.toFixed(1)} ${unit}`, "Tide"]}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              labelFormatter={(_: any, payload: any) => {
                const d = payload?.[0]?.payload as ChartDatum | undefined;
                if (!d) return "";
                return `${d.dayLabel} ${d.label}`.toUpperCase();
              }}
            />

            {nowIdx !== null && (
              <ReferenceLine
                x={nowIdx}
                stroke={theme.palette.accent.main}
                strokeWidth={1}
                label={{
                  value: "NOW",
                  position: "top",
                  offset: -10,
                  dx: 15,
                  fill: theme.palette.accent.main,
                  fontSize: 10,
                  fontFamily: fontStack.sans,
                  letterSpacing: 1,
                }}
              />
            )}

            {hiLoLabels.map((hl, i) => (
              <ReferenceLine
                key={`hilo-${i}`}
                x={hl.idx}
                stroke="transparent"
                label={{
                  value: `${hl.type === "H" ? "H" : "L"} ${hl.height.toFixed(1)}`,
                  position: hl.type === "H" ? "top" : "bottom",
                  fill: theme.palette.ink.muted,
                  fontSize: 10,
                  fontFamily: fontStack.mono,
                }}
              />
            ))}

            <Area
              type="monotone"
              dataKey="height"
              stroke={theme.palette.chart.swell}
              strokeWidth={1.5}
              fill="url(#tideFill)"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
